import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { listDevices } from '../lib/audio';
import { forgetPttButton } from '../lib/btptt';
import { isNative, nativePlatform } from '../lib/native';
import { session } from '../lib/session';
import { updateSettings, useStore, type BtPttState } from '../store';
import { InterfaceSettings } from './InterfaceSettings';
import { Drawer, Field, Toggle } from './ui';

export function SettingsDrawer({ onClose }: { onClose: () => void }) {
  const settings = useStore((s) => s.settings);
  const media = useStore((s) => s.media);
  const btState = useStore((s) => s.btPttState);
  const btName = useStore((s) => s.btPttName);
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listDevices()
      .then((d) => {
        setInputs(d.inputs);
        setOutputs(d.outputs);
      })
      .catch(() => undefined);
  }, [media]);

  const restartMic = async () => {
    setBusy(true);
    try {
      await session.restartMic();
    } finally {
      setBusy(false);
    }
  };

  const supportsSink = 'setSinkId' in AudioContext.prototype;

  return (
    <Drawer title="Settings" onClose={onClose}>
      <div className="drawer__body">
        <div className="section-title">Microphone</div>
        <Field label="Input device">
          <select className="select" value={settings.micDeviceId ?? ''} onChange={(e) => updateSettings({ micDeviceId: e.target.value || null })}>
            <option value="">System default</option>
            {inputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
        </Field>
        <Toggle label="Echo cancellation" on={settings.echoCancellation} onChange={(v) => updateSettings({ echoCancellation: v })} />
        <Toggle label="Noise suppression" on={settings.noiseSuppression} onChange={(v) => updateSettings({ noiseSuppression: v })} />
        <Toggle label="Auto gain control" on={settings.autoGainControl} onChange={(v) => updateSettings({ autoGainControl: v })} />
        <button className="btn btn--neutral btn--block" onClick={() => void restartMic()} disabled={busy} style={{ marginTop: 8 }}>
          <RefreshCw size={16} /> {busy ? 'Re-opening…' : 'Apply microphone settings'}
        </button>

        <div className="section-title">Output</div>
        {nativePlatform() === 'ios' && (
          <Toggle
            label="Loudspeaker"
            help="Off = earpiece. Headsets and Bluetooth take priority when connected."
            on={settings.speaker}
            onChange={(v) => {
              updateSettings({ speaker: v });
              void session.applyAudioSettings();
            }}
          />
        )}
        {supportsSink ? (
          <Field label="Output device">
            <select
              className="select"
              value={settings.outputDeviceId ?? ''}
              onChange={(e) => {
                updateSettings({ outputDeviceId: e.target.value || null });
                void session.applyAudioSettings();
              }}
            >
              <option value="">System default</option>
              {outputs.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `Output ${d.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="field__help">Output device selection is not supported by this browser (uses system default).</p>
        )}
        <Field label={`Master volume · ${Math.round(settings.masterVolume * 100)}%`}>
          <input
            className="range"
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={settings.masterVolume}
            onChange={(e) => {
              updateSettings({ masterVolume: Number(e.target.value) });
              void session.applyAudioSettings();
            }}
          />
        </Field>
        <Field label={`Sidetone · ${settings.sidetone === 0 ? 'off' : `${Math.round(settings.sidetone * 100)}%`}`} help="Hear your own mic in your headset. Keep off on speakers.">
          <input
            className="range"
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={settings.sidetone}
            onChange={(e) => {
              updateSettings({ sidetone: Number(e.target.value) });
              void session.applyAudioSettings();
            }}
          />
        </Field>

        <InterfaceSettings />

        <div className="section-title">Behaviour</div>
        <Toggle label="Key click sounds" on={settings.clicks} onChange={(v) => updateSettings({ clicks: v })} />
        <Toggle label="Call chime" on={settings.chimes} onChange={(v) => updateSettings({ chimes: v })} />
        <Toggle label="Keyboard hotkeys (1–0 talk, Esc release)" on={settings.hotkeys} onChange={(v) => updateSettings({ hotkeys: v })} />
        <Toggle label="Keep screen awake" on={settings.keepAwake} onChange={(v) => updateSettings({ keepAwake: v })} />

        {isNative() && (
          <>
            <div className="section-title">Bluetooth PTT button</div>
            <Toggle
              label="Use a Bluetooth PTT button"
              help="PrymeBLU PTT-Z / ZU and other Zello-style buttons. The button works like the dial's centre TALK key: hold to talk on the armed keys."
              on={settings.btPtt}
              onChange={(v) => updateSettings({ btPtt: v })}
            />
            {settings.btPtt && (
              <>
                <p className="field__help" role="status">{btStatusText(btState, btName)}</p>
                {settings.btPttDevice && (
                  <button className="btn btn--neutral btn--block" onClick={() => void forgetPttButton()} style={{ marginTop: 8 }}>
                    Forget this button and pair another
                  </button>
                )}
              </>
            )}
          </>
        )}

        <div className="section-title">VOX (voice-activated talk)</div>
        <Toggle
          label="Enable VOX"
          help="Keys marked VOX in the panel editor talk automatically when your mic crosses the threshold below — no PTT needed."
          on={settings.voxEnabled}
          onChange={(v) => updateSettings({ voxEnabled: v })}
        />
        <Field label={`Threshold · ${settings.voxThreshold} dB`} help="Lower = more sensitive. Watch the MIC meter in the header while adjusting.">
          <input className="range" type="range" min={-60} max={-10} step={1} value={settings.voxThreshold} onChange={(e) => updateSettings({ voxThreshold: Number(e.target.value) })} />
        </Field>
        <Field label={`Hang time · ${settings.voxHangMs} ms`} help="How long VOX keeps talking after you drop below threshold, so it doesn't chop off pauses mid-sentence.">
          <input className="range" type="range" min={100} max={2000} step={50} value={settings.voxHangMs} onChange={(e) => updateSettings({ voxHangMs: Number(e.target.value) })} />
        </Field>

        <div className="section-title">Program feed input</div>
        <Field label="Program input device" help="Line input used when feeding program audio into IFB channels. Processing is disabled for this input.">
          <select className="select" value={settings.programDeviceId ?? ''} onChange={(e) => updateSettings({ programDeviceId: e.target.value || null })}>
            <option value="">System default</option>
            {inputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Input ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Drawer>
  );
}

function btStatusText(state: BtPttState, name: string | null): string {
  switch (state) {
    case 'scanning':
      return 'Searching. Switch the button on and press it once to wake it.';
    case 'connecting':
      return `Connecting to ${name ?? 'the button'}… Press it to wake it if it has gone to sleep.`;
    case 'connected':
      return `Connected: ${name ?? 'PTT button'}.`;
    case 'unauthorized':
      return nativePlatform() === 'ios'
        ? 'Bluetooth access is off. Turn it on in the Settings app → BCComms → Bluetooth.'
        : 'Bluetooth permission was denied. Allow "Nearby devices" for BCComms in Android Settings → Apps.';
    case 'unavailable':
      return 'Bluetooth is off or not available on this device.';
    default:
      return 'Starting…';
  }
}
